import { useMemo, useState } from "react";

import type { MemberRevenueStatisticVo } from "@zhihaoscm/entity";
import { dashboardService } from "@/api/services/dashboardService";
import { formatThousandSeparator } from "@/utils/money";
import DashboardChart from "../chart";
import { getAnnularOption } from "../chartDefaultOption";
import {
    DashboardDateSelect,
    type DashboardDateValue,
    DataWrap,
    toDashboardDateParams,
    useDashboardQuery,
    WidgetBody
} from "../dashboardWidgets";

const REVENUE_COLORS = ["#1777ff", "#ffae4c", "#6fd195"];

/** 会员营收统计，严格沿用 Vue 的大号总费用和会员费用环形图。 */
export default function MemberRevenueStatistic() {
    const [time, setTime] = useState<DashboardDateValue>(1);
    const { data, loading, error } = useDashboardQuery<Partial<MemberRevenueStatisticVo>>(
        () => dashboardService.getMemberRevenueStatistic(toDashboardDateParams(time)),
        [time]
    );
    const seriesData = useMemo(
        () => [
            { value: data?.totalBaseMemberCost || 0, name: "基础会员" },
            { value: data?.totalSeniorMemberCost || 0, name: "高级会员" },
            { value: data?.totalEnterpriseGradeMemberCost || 0, name: "企业会员" }
        ],
        [data]
    );
    const total = seriesData.reduce((sum, item) => sum + item.value, 0);
    const option = useMemo(
        () =>
            getAnnularOption({
                legend: {
                    left: "50%",
                    itemGap: 24,
                    textStyle: {
                        rich: {
                            a: { width: 80, color: "#333333", fontSize: 14 },
                            b: { color: "#333333", fontSize: 14 }
                        }
                    },
                    formatter(name: string) {
                        const item = seriesData.find(seriesItem => seriesItem.name === name);
                        return item ? `{a|${name}}{b| ${formatThousandSeparator(item.value)}万元}` : name;
                    }
                },
                series: [
                    {
                        radius: ["65%", "85%"],
                        center: ["25%", "55%"],
                        data: seriesData,
                        itemStyle: {
                            color(params: { dataIndex: number }) {
                                return REVENUE_COLORS[params.dataIndex % REVENUE_COLORS.length];
                            }
                        },
                        emphasis: {
                            label: {
                                show: true,
                                formatter(item: { name: string; value: number }) {
                                    const rate = total ? (item.value / total) * 100 : 0;
                                    return [`{a|${item.name}}`, `{b|${formatThousandSeparator(rate)}%}`].join("\n");
                                },
                                rich: {
                                    a: { color: "#999999e6", fontSize: 14, lineHeight: 25 },
                                    b: { color: "#333333e6", fontSize: 20, verticalAlign: "bottom" }
                                }
                            }
                        }
                    }
                ]
            }),
        [seriesData, total]
    );

    return (
        <DataWrap
            title="会员营收统计"
            className="member-revenue-statistic"
            option={
                <DashboardDateSelect
                    value={time}
                    onChange={setTime}
                    options={["all", "month", "quarter", "year"]}
                    defaultActive="all"
                />
            }
        >
            <WidgetBody loading={loading} error={error}>
                <div className="member-revenue-main">
                    <div className="dashboard-statistic-text-box">
                        <span className="dashboard-statistic-label">累计会员总费用</span>
                        <span className="dashboard-statistic-value">
                            {formatThousandSeparator(data?.totalMemberCost || 0)}
                            <i>万元</i>
                        </span>
                    </div>
                    <div className="member-revenue-chart">
                        <DashboardChart option={option} ariaLabel="会员费用构成环形图" />
                    </div>
                </div>
            </WidgetBody>
        </DataWrap>
    );
}
